import { Inject, Injectable } from '@angular/core';
import {
    EncodingUtilities,
    NotificationIdentifier,
    NotificationInterface,
    spqDocRefreshRequired$,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Store } from '@ngrx/store';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, mergeMap, take, tap } from 'rxjs/operators';
import { NotificationInstanceToken } from '../core/interfaces/notification.interface';
import { saveForm } from '../store/actions/form.actions';
import { getForm } from '../store/selectors/form.selector';
import { FormState } from '../store/state/form.state';
import { FormsState } from '../store/state/pace-forms.state';
import { ContentResults } from '../types/content-results';
import { FormIdentifier } from '../types/form-identifier';
import { initialFormTypeFilters } from '../types/form-type-filters';
import { FormsConstants } from '../types/forms-constants';
import { ODataSPQModel } from '../types/service-part-questionnaire';
import { FormsStorageService } from './forms-storage.service';
import { OdataServicePartQuestionnaireService } from './odata-service-part-questionnaire.service';
import { XmlParserService } from './xml-parser.service';
import { XmlSerializerService } from './xml-serializer.service';
import FORMTYPE = FormsConstants.FORMTYPE;

@Injectable({
    providedIn: 'root',
})
export class ServicePartQuestionnaireService {
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly formsStorageService: FormsStorageService,
        @Inject(NotificationInstanceToken) private readonly notificationService: NotificationInterface,
        private readonly xmlParserService: XmlParserService,
        private readonly xmlSerializerService: XmlSerializerService,
        private readonly odataServicePartQuestionnaireService: OdataServicePartQuestionnaireService,
        private readonly store: Store<FormsState>,
    ) {
        spqDocRefreshRequired$()
            .pipe(mergeMap(() => this.getAllQuestionnaires()))
            .subscribe();
    }

    public getAllQuestionnaires(): Observable<ODataSPQModel[]> {
        const docIds = this.notificationService.getAllSPQDocIDs();

        return forkJoin(docIds.map((docId) => this.getQuestionnaireFromCache(docId))).pipe(take(1));
    }

    public getQuestionnaireFromCache(docId: string): Observable<ODataSPQModel> {
        return this.formsStorageService.loadQuestionnaireXmlFromCache(docId).pipe(
            tap(() =>
                this.log.info(
                    `ServicePartQuestionnaireService: getQuestionnaireFromCache: questionnaire ${docId} successfully loaded from cache`,
                ),
            ),
            catchError(() => {
                this.log.info(
                    `ServicePartQuestionnaireService: getQuestionnaireFromCache: questionnaire ${docId} is not cached yet, downloading now`,
                );
                // SPQ is not cached locally
                // So get it from backend
                return this.odataServicePartQuestionnaireService.getQuestionnaireFromBackend(docId).pipe(
                    mergeMap((odata) => {
                        return this.formsStorageService.saveQuestionnaireXmlToCache(docId, odata);
                    }),
                );
            }),
        );
    }

    public setSpqXml(content: string, notification: NotificationIdentifier, materialKey: string): ContentResults {
        const spqContent = EncodingUtilities.base64DecodeUnicode(content);
        this.xmlParserService.setXml(spqContent);

        const servicePartQuestionnaireContentResults = this.xmlParserService.getContent(
            undefined,
            FORMTYPE.REPLACEMENT_REPORT,
            initialFormTypeFilters,
            notification,
            false,
            true,
            FormsConstants.LOAD_STATUS.TEMPLATE,
        );

        const defectServicePartIdentification = this.xmlSerializerService.updateDefectServicePartIdentification(
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore --- @TEO can you please check that?
            servicePartQuestionnaireContentResults.defectServicePartIdentification, // the defective service part identification is always available on the service part questionnaires
            materialKey,
        );

        return {
            ...servicePartQuestionnaireContentResults,
            defectServicePartIdentification,
        };
    }

    public removeQuestionnaireCopyDataFromCache(
        notification: NotificationIdentifier,
        form: FormIdentifier,
        materialKey: string,
    ): Observable<void> {
        this.log.info(
            `ServicePartQuestionnaireService: removeQuestionnaireCopyDataFromCache: removing the questionnaire copy data ${notification.qmNum}-${notification.maNum}-${form.materialId}-${materialKey}-${form.formId} from cache!`,
        );
        return this.formsStorageService
            .removeSPQSubmitStatus(notification, form, materialKey)
            .pipe(mergeMap(() => this.checkAndRemoveUnneededQuestionnaire(form.formId)));
    }

    public checkAndRemoveUnneededQuestionnaire(docId: string): Observable<void> {
        const isQuestionnaireNotNeeded = !this.notificationService.getAllSPQDocIDs().includes(docId);

        if (isQuestionnaireNotNeeded) {
            this.log.debug(
                `ServicePartQuestionnaireService: checkAndRemoveUnneededQuestionnaire: removing questionnaire ${docId} from cache, as it is not needed anymore.`,
            );
            return this.formsStorageService.removeQuestionnaireXmlFromCache(docId);
        } else {
            return of(undefined);
        }
    }

    public getQuestionnaireState(): Observable<FormState> {
        return this.store.select(getForm).pipe(
            take(1),
            tap(() => this.store.dispatch(saveForm())),
        );
    }
}
